import React, { useState, useEffect } from "react";
import { Card, Row, Col, Select, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import ReportService from "../../services/ReportService";

const monthLabels = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1; // 1-12

type CompanyMonthTotal = {
  [key: string]: number; // month1 ... month12, total
};

type CompanyMonthRow = {
  firstName: string;
  lastName: string;
  email: string;
  accountId: number;
  total: number;
  [key: string]: any; // month1, month2, ... month12
};

const MonthlyCompanyDashboard: React.FC = () => {
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [total, setTotal] = useState<CompanyMonthTotal | null>(null);
  const [companyData, setCompanyData] = useState<CompanyMonthRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async (year: number) => {
    setLoading(true);
    try {
      const [totalRes, companyRes] = await Promise.all([
        ReportService.getMonthlyCompanyCountsTotal(year),
        ReportService.getMonthlyCompanyCounts(year)
      ]);
      setTotal(totalRes.data);
      setCompanyData(companyRes.data);
    } catch {
      setTotal(null);
      setCompanyData([]);
      message.error("Veri alınırken hata oluştu!");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(year);
    // eslint-disable-next-line
  }, [year]);

  // Hangi ay future? (bugünkü aydan büyükse ve yıl güncel yıl ise)
  const isFutureMonth = (monthIdx: number) =>
    year === CURRENT_YEAR && monthIdx + 1 > CURRENT_MONTH;

  // Kart için önceki ay artış/azalış kontrolü (future ise ikon gösterme)
  const getDeltaIcon = (curr: number, prev?: number, idx?: number) => {
    if (typeof idx === "number" && isFutureMonth(idx)) return null;
    if (prev === undefined) return null;
    if (curr > prev)
      return <ArrowUpOutlined style={{ color: "#389e0d", marginLeft: 3, fontSize: 18 }} />;
    if (curr < prev)
      return <ArrowDownOutlined style={{ color: "#cf1322", marginLeft: 3, fontSize: 18 }} />;
    return null;
  };

  // Tablo hücrelerinde ok göstermek için render fonksiyonu (future ise 0 ve ok yok)
  const renderWithDelta = (row: CompanyMonthRow, i: number) => {
    const value = isFutureMonth(i) ? 0 : row[`month${i + 1}`];
    const prevValue = i > 0 ? (isFutureMonth(i - 1) ? 0 : row[`month${i}`]) : undefined;
    return (
      <span>
        {value > 0
          ? <b>{value}</b>
          : <span style={{ color: "#aaa" }}>{value}</span>}
        {i > 0 && !isFutureMonth(i) && prevValue !== undefined && value !== undefined && value !== prevValue &&
          (value > prevValue
            ? <ArrowUpOutlined style={{ color: "#389e0d", marginLeft: 3, fontSize: 13 }} />
            : <ArrowDownOutlined style={{ color: "#cf1322", marginLeft: 3, fontSize: 13 }} />)
        }
      </span>
    );
  };

  const columns: ColumnsType<CompanyMonthRow> = [
    {
      title: "Ad Soyad / E-posta",
      render: (_, r) => (
        <>
          {r.firstName} {r.lastName}
          <br />
          <span style={{ color: "#888", fontSize: 12 }}>{r.email}</span>
        </>
      ),
      key: "adsoyad",
      width: 220,
      fixed: "left"
    },
    ...Array.from({ length: 12 }).map((_, i) => ({
      title: monthLabels[i],
      dataIndex: `month${i + 1}`,
      key: `month${i + 1}`,
      align: "center" as "center",
      width: 80,
      render: (_: any, row: CompanyMonthRow) => renderWithDelta(row, i)
    })),
    {
      title: <b>Toplam</b>,
      dataIndex: "total",
      key: "total",
      align: "center" as "center",
      width: 80,
      render: (value: number) =>
        value > 0
          ? <b>{value}</b>
          : <span style={{ color: "#aaa" }}>{value}</span>
    }
  ];

  return (
    <div>
      <Card
        title="Firma Kayıt Raporu"
        extra={
          <Select
            value={year}
            onChange={setYear}
            style={{ minWidth: 100 }}
            options={Array.from({ length: 6 }).map((_, i) => {
              const y = CURRENT_YEAR - i;
              return { label: y, value: y };
            })}
          />
        }
      >
        {/* Dashboard Card */}
        {total && (
          <Row gutter={[8, 8]} justify={"space-between"} style={{ marginBottom: 24 }}>
            {Array.from({ length: 12 }).map((_, i) => {
              const isFuture = isFutureMonth(i);
              const value = isFuture ? 0 : total[`month${i + 1}`] ?? 0;
              const prevValue = i > 0 ? (isFutureMonth(i - 1) ? 0 : total[`month${i}`] ?? 0) : undefined;
              return (
                <Col key={i}>
                  <Card
                    bordered={false}
                    style={{
                      background: "#f7f7fa",
                      textAlign: "center",
                      width: "100%"
                    }}
                  >
                    <div style={{ fontSize: 14, color: "#888" }}>{monthLabels[i]}</div>
                    <div style={{ fontWeight: 700, fontSize: 28, color: "#1764b2" }}>
                      {value}
                      {i > 0 && !isFuture && getDeltaIcon(value, prevValue, i)}
                    </div>
                  </Card>
                </Col>
              );
            })}
            <Col>
              <Card
                bordered={false}
                style={{
                  background: "#e6f4ff",
                  textAlign: "center",
                  width: 110,
                  border: "1.5px solid #1764b2"
                }}
              >
                <div style={{ fontSize: 14, color: "#1764b2" }}>Toplam</div>
                <div style={{ fontWeight: 900, fontSize: 32, color: "#1764b2" }}>
                  {total.total}
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Tablo */}
        <Table
          columns={columns}
          dataSource={companyData}
          loading={loading}
          rowKey="accountId"
          scroll={{ x: "max-content" }}
          bordered
          size="small"
          pagination={false}
          locale={{
            emptyText: "Veri bulunamadı. Lütfen yıl seçip sorgulayınız."
          }}
        />
      </Card>
    </div>
  );
};

export default MonthlyCompanyDashboard;
